import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../api';
import { normalizeEntryDate } from '../utils/bruto';
import { PROJECT_STATUSES, projectStatusMeta, normalizeProjectStatus } from '../utils/projectStatus';


const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthKey = (date) => { const iso = normalizeEntryDate(String(date || '')); return iso ? iso.slice(0, 7) : ''; };
const monthLabel = (key) => `${MONTH_NAMES[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`;

const fmt = (n) => (Number(n) || 0).toFixed(1);
const fmtDate = (d) => {
  if (!d) return '';
  const iso = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : d;
};

function ProjectCard({ project }) {
  const [open, setOpen] = useState(false);
  const status = projectStatusMeta(project.status);

  // Subtotals per category (Bruto only — Neto is never sent to this role).
  const byCategory = useMemo(() => {
    const map = {};
    project.lines.forEach((l) => { map[l.category] = (map[l.category] || 0) + l.bruto; });
    return Object.entries(map);
  }, [project.lines]);

  return (
    <article className="bg-white rounded-lg shadow p-5 text-gray-900">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">{project.name}</h2>
          <p className="text-sm text-gray-500">{project.client}</p>
        </div>
        <span className={`text-xs font-semibold px-2 py-1 rounded ${status.cls}`}>{status.label}</span>
      </header>
      <p className="mt-3 text-2xl font-bold">{fmt(project.total_bruto)} <span className="text-sm font-normal text-gray-500">hours</span></p>
      <ul className="mt-2 text-sm text-gray-700">
        {byCategory.map(([cat, hours]) => (
          <li key={cat} className="flex justify-between border-b border-gray-100 py-1"><span>{cat}</span><span>{fmt(hours)} h</span></li>
        ))}
      </ul>
      {project.lines.length > 0 && (
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          className="mt-3 text-sm text-indigo-600 hover:text-indigo-800">
          {open ? 'Hide details' : 'Show details'}
        </button>
      )}
      {open && (
        <ul className="mt-2 space-y-3">
          {project.lines.map((l, i) => (
            <li key={i} className="text-sm border-l-2 border-indigo-200 pl-3">
              <div className="flex justify-between gap-2">
                <span className="font-semibold">{l.category}{l.task_label ? ` – ${l.task_label}` : ''}</span>
                <span>{fmt(l.bruto)} h</span>
              </div>
              <div className="text-xs text-gray-500">{l.user}{l.date ? `, ${fmtDate(l.date)}` : ''}</div>
              {l.work_note && <p className="mt-1 whitespace-pre-wrap break-words text-gray-700">{l.work_note}</p>}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

// Read-only portal for requesters ("requesters"): only their own projects, Bruto hours only.
export default function RequesterPortal({ loggedInUser, onLogout }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');   // 'YYYY-MM', '' = all months
  const [clientFilter, setClientFilter] = useState(''); // '' = all clients

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/my-projects`)
      .then((res) => setData(res.data))
      .catch(() => setError('Could not load projects. Refresh the page.'));
  }, []);

  const allProjects = data ? data.projects : [];
  const clients = useMemo(() => [...new Set(allProjects.map((p) => p.client).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [allProjects]);
  const months = useMemo(
    () => [...new Set(allProjects.flatMap((p) => p.lines.map((l) => monthKey(l.date))).filter(Boolean))].sort().reverse(),
    [allProjects]
  );

  // Month filter narrows each project to that month's work (totals follow); projects with none are hidden.
  const projects = allProjects
    .filter((p) => (!statusFilter || normalizeProjectStatus(p.status) === statusFilter) && (!clientFilter || p.client === clientFilter))
    .map((p) => {
      if (!monthFilter) return p;
      const lines = p.lines.filter((l) => monthKey(l.date) === monthFilter);
      return { ...p, lines, total_bruto: Math.round(lines.reduce((sum, l) => sum + l.bruto, 0) * 100) / 100 };
    })
    .filter((p) => !monthFilter || p.lines.length > 0);
  const filtersActive = Boolean(statusFilter || monthFilter || clientFilter);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow px-6 py-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">My projects</h1>
        <div className="text-sm text-gray-700">
          <span className="mr-3">{loggedInUser}</span>
          <button onClick={onLogout} className="bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700">Log out</button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto p-6">
        {error && <p className="text-red-600" role="alert">{error}</p>}
        {!error && !data && <p className="text-gray-500">Loading…</p>}
        {data && data.projects.length === 0 && (
          <p className="text-gray-600">There are currently no projects logged under your name.</p>
        )}
        {data && data.projects.length > 0 && (
          <>
            <div className="mb-4 flex flex-wrap gap-2 items-center">
              <select aria-label="Month" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}
                className="border p-2 rounded bg-white text-gray-900">
                <option value="">All months</option>
                {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
              </select>
              {clients.length > 1 && (
                <select aria-label="Client" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
                  className="border p-2 rounded bg-white text-gray-900">
                  <option value="">All clients</option>
                  {clients.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
              <select aria-label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="border p-2 rounded bg-white text-gray-900">
                <option value="">All statuses</option>
                {PROJECT_STATUSES.map((st) => <option key={st.value} value={st.value}>{st.label}</option>)}
              </select>
              {filtersActive && (
                <button type="button" onClick={() => { setMonthFilter(''); setClientFilter(''); setStatusFilter(''); }}
                  className="text-sm text-indigo-600 hover:text-indigo-800 underline">Reset filters</button>
              )}
              {monthFilter && (
                <span className="text-sm text-gray-600 ml-auto">
                  Total {monthLabel(monthFilter)}: <strong>{fmt(projects.reduce((sum, p) => sum + p.total_bruto, 0))} h</strong>
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {projects.map((p) => <ProjectCard key={p.id} project={p} />)}
            </div>
            {projects.length === 0 && <p className="text-gray-600 mt-4">No projects match the selected filters.</p>}
          </>
        )}
      </main>
    </div>
  );
}
